import Polyline from "@arcgis/core/geometry/Polyline";
import ElevationLayer from "@arcgis/core/layers/ElevationLayer";
import ImageryLayer from "@arcgis/core/layers/ImageryLayer.js";
import * as lengthOperator from "@arcgis/core/geometry/operators/lengthOperator.js";
import * as densifyOperator from "@arcgis/core/geometry/operators/densifyOperator.js";
import { AppConfig } from "../AppConfig";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import { getMapView } from "./arcgisUtils";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Multipoint from "@arcgis/core/geometry/Multipoint.js";
import { toast } from "react-toastify";
import LabelClass from "@arcgis/core/layers/support/LabelClass";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import MeasureFromImageParameters from "@arcgis/core/rest/support/MeasureFromImageParameters";
import type Extent from "@arcgis/core/geometry/Extent";

export const createSeaDepthElevationLayer = async () => {
    const depthLayer = new ElevationLayer({
        url: "https://services.geodataonline.no/arcgis/rest/services/Geocache_UTM33_EUREF89/GeocacheDybdedata/ImageServer",
    });

    await depthLayer.load();
    return depthLayer;
};

export const createTerrainElevationLayer = async () => {
    const terrainLayer = new ElevationLayer({
        url: AppConfig.HeightmapUrl,
    });

    await terrainLayer.load();
    return terrainLayer;
};

export const queryDepthElevation = async (
    geometry: Polyline,
    depthLayer?: ElevationLayer,
    terrainLayer?: ElevationLayer
) => {
    depthLayer ??= await createSeaDepthElevationLayer();
    terrainLayer ??= await createTerrainElevationLayer();

    const denseGeometry = densifyOperator.execute(geometry, 1, { unit: "meters" }) as Polyline;
    // Gets a new polyline from depthLayer with elevation/depth values
    const depthResponse = await depthLayer.queryElevation(denseGeometry, { demResolution: 1 });
    const terrainResponse = await terrainLayer.queryElevation(denseGeometry, { demResolution: 1 });

    const depthGeometry = depthResponse.geometry as Polyline;
    const terrainGeometry = terrainResponse.geometry as Polyline;
    let combinedGeometry: Polyline | undefined = undefined;
    if (depthGeometry) {
        const combinedPaths: number[][] = [];
        if (terrainGeometry && depthGeometry.paths[0].length === terrainGeometry.paths[0].length) {
            for (let i = 0; i < terrainGeometry.paths[0].length; i++) {
                if (terrainGeometry.paths[0][i][2] === 0) {
                    combinedPaths.push(depthGeometry.paths[0][i]);
                } else {
                    combinedPaths.push(terrainGeometry.paths[0][i]);
                }
            }
        }

        if (combinedPaths.length > 0) {
            // Create a new polyline with the combined paths
            combinedGeometry = new Polyline({
                paths: [combinedPaths],
                spatialReference: depthGeometry.spatialReference,
            });
        }

        // Does not take z-values into consideration 😡
        // const actualLength1 = lengthOperator.execute(response.geometry, { unit: "meters" });
        const actualLength = calculateActual3DLength(
            combinedGeometry?.paths ?? depthGeometry.paths,
            combinedGeometry?.spatialReference ?? depthGeometry.spatialReference
        );
        return { actualLength, detailedGeometry: combinedGeometry };
    }
    return { actualLength: -1, detailedGeometry: undefined };
};

const calculateActual3DLength = (paths: number[][][], spatialReference: SpatialReference) => {
    let totalLength = 0;
    const line = paths[0];

    for (let i = 0; i < line.length - 1; i++) {
        const start = line[i];
        const end = line[i + 1];
        const segmentLength = calculateHypothenuseFromTwoPoints(start, end, spatialReference);
        totalLength += segmentLength;
    }

    return totalLength;
};

export const createSeaDepthImageryLayer = async () => {
    const imageryLayer = new ImageryLayer({
        url: "https://services.geodataonline.no/arcgis/rest/services/Geocache_UTM33_EUREF89/GeocacheDybdedata/ImageServer",
    });

    await imageryLayer.load();
    return imageryLayer;
};

export const querySeaDepthImagery = async (geometry: Polyline, imageryLayer?: ImageryLayer) => {
    imageryLayer ??= await createSeaDepthImageryLayer();
    // This function is not supported on the geocacheDybde service atm the moment.
    const response = await imageryLayer.measureLengthFromImage(new MeasureFromImageParameters({ geometry: geometry }));
    return response;
};

export const getAggregatingLineLengths = (geometry: Polyline) => {
    const paths = geometry.paths;
    let aggregatedLengths: number[] = [];

    let cumulativeLength = 0;

    for (const element of paths) {
        const line = element;
        for (let j = 0; j < line.length - 1; j++) {
            const start = line[j];
            const end = line[j + 1];
            const linePartLength = lengthOperator.execute(
                new Polyline({
                    paths: [
                        [
                            [start[0], start[1]],
                            [end[0], end[1]],
                        ],
                    ],
                    spatialReference: geometry.spatialReference,
                }),
                { unit: "meters" }
            );
            cumulativeLength += linePartLength;
            aggregatedLengths.push(cumulativeLength);
        }
    }

    return aggregatedLengths;
};

export const getAggregatingSegmentLengths = (geometry: Polyline) => {
    const paths = geometry.paths;
    const aggregatedLengths: number[] = [0]; // Start with 0 for the first point

    let cumulativeLength = 0;

    for (const element of paths) {
        const line = element;
        for (let j = 0; j < line.length - 1; j++) {
            const start = line[j];
            const end = line[j + 1];
            const segmentLength = calculateHypothenuseFromTwoPoints(start, end, geometry.spatialReference);
            cumulativeLength += segmentLength;
            aggregatedLengths.push(cumulativeLength);
        }
    }

    return aggregatedLengths;
};

export const getPointHeights = (geometry: Polyline) => {
    const heights: number[] = [];
    const paths = geometry.paths;

    for (const element of paths) {
        const line = element;
        for (const point of line) {
            heights.push(point[2]);
        }
    }

    return heights;
};

const calculateHypothenuseFromTwoPoints = (point1: number[], point2: number[], spatialReference: SpatialReference) => {
    if (point1.length !== 3 || point2.length !== 3) {
        return -1;
    }
    // Length of side A is height diffrence between points
    const lengthA = Math.abs(Math.abs(point1[2]) - Math.abs(point2[2]));
    // Length of side B is distance between points
    const lengthB = lengthOperator.execute(
        new Polyline({
            paths: [
                [
                    [point1[0], point1[1]],
                    [point2[0], point2[1]],
                ],
            ],
            spatialReference: spatialReference,
        }),
        { unit: "meters" }
    );

    return Math.hypot(lengthA, lengthB);
};

/**
 * Queries depth values for a given extent and renders them to the map as labels.
 * Uses the GeodataOnline Geocache Dybdedata service.
 * @param extent
 * @returns
 */
export const queryHighResDepth = async (extent: Extent) => {
    const mapview = getMapView();
    if (!mapview?.map) throw new Error("No mapview");
    if (mapview.scale >= 200) {
        renderPointsToMap([]);
        return;
    }

    const mapviewDepthLayer = mapview.map.findLayerById("highResDepthLayer") as FeatureLayer;
    if (mapviewDepthLayer) {
        if (!mapviewDepthLayer.visible) {
            return;
        }
    }

    // const depthLayerUrl = "https://maps.kystverket.cloudgis.no/enterprise/rest/services/DEV/dybdedata_dev/ImageServer";
    const depthLayerUrl =
        "https://services.geodataonline.no/arcgis/rest/services/Geocache_UTM33_EUREF89/GeocacheDybdedata/ImageServer";
    const depthLayer = new ElevationLayer({
        url: depthLayerUrl,
    });
    if (!depthLayer.loaded) {
        await depthLayer.load();
    }

    const depthImageLayer = new ImageryLayer({
        url: depthLayerUrl,
    });
    if (!depthImageLayer.loaded) {
        await depthImageLayer.load();
    }

    const depthPoints: number[][] = [];
    if (extent.spatialReference.unit !== "meters") {
        toast.error("Kartet må være i meter (f.eks UTM33) for å hente dybdeverdier.");
        return;
    }

    // Length of number 1 is no decimals, 2 is one decimal (0.5), 3 is two decimals (0.25)
    const numberlength = 1;

    // Round to nearest 0.5 to reduce number of points
    extent.xmin = Math.round(extent.xmin * numberlength) / numberlength;
    extent.xmax = Math.round(extent.xmax * numberlength) / numberlength;
    extent.ymin = Math.round(extent.ymin * numberlength) / numberlength;
    extent.ymax = Math.round(extent.ymax * numberlength) / numberlength;

    for (let x = extent.xmin; x <= extent.xmax; x += 0.5) {
        for (let y = extent.ymin; y <= extent.ymax; y += 0.5) {
            depthPoints.push([x, y]);
        }
    }

    const depthMultipoint = new Multipoint({
        points: depthPoints,
        spatialReference: extent.spatialReference,
    });

    // const result = await depthImageLayer.identify({
    //     //@ts-ignore
    //     geometry: depthMultipoint,
    //     returnGeometry: false,
    //     returnCatalogItems: false,
    //     returnPixelValues: true,
    //     maxItemCount: 10000
    // });
    // console.log(result);

    const depths = await depthLayer.queryElevation(depthMultipoint, { demResolution: 1 });
    // console.log(depths);
    const geometry = depths.geometry as Multipoint;
    const pointValues = geometry.points;
    const features: Graphic[] = [];
    const uniquePositions = new Set<string>();
    const maxFeaturesToRender = 10000;
    for (const element of pointValues) {
        if (features.length >= maxFeaturesToRender) {
            console.warn("Reached max label features to render");
            break;
        }
        const point = element;
        if (point[2] === -9999 || point[2] === null || point[2] === undefined) continue; // No data value
        const x = Math.round(point[0] * numberlength) / numberlength;
        const y = Math.round(point[1] * numberlength) / numberlength;
        const posKey = `${x}_${y}`;
        if (uniquePositions.has(posKey)) continue;
        uniquePositions.add(posKey);

        // Using x_y_ as objectid to avoid duplicates
        features.push(
            new Graphic({
                geometry: new Point({ x, y, spatialReference: extent.spatialReference }),
                attributes: { dybde: point[2], uniquePos: posKey },
            })
        );
    }

    renderPointsToMap(features);
};

/**
 * Renders the depth values to the map as points in a feature layer
 */
export const renderPointsToMap = async (points: Graphic[]) => {
    const mapview = getMapView();
    if (!mapview?.map) throw new Error("No mapview");

    let depthLayer = mapview.map.findLayerById("highResDepthLayer") as FeatureLayer;

    const depthLabel = new LabelClass({
        labelExpressionInfo: {
            expression: "return Round($feature.dybde, 2)",
        },
        labelPlacement: "center-center",
        symbol: {
            type: "text",
            color: [0, 0, 0, 1],
            // haloColor: [0, 0, 0, 0.7],
            // haloSize: 2,
            font: {
                size: 8,
                family: "sans-serif",
            },
        },
    });

    if (!depthLayer) {
        depthLayer = new FeatureLayer({
            id: "highResDepthLayer",
            source: points,
            outFields: ["*"],
            objectIdField: "objectid",
            title: "Dydbdeverdier (punkter)",
            fields: [
                { name: "objectid", type: "oid" },
                { name: "dybde", type: "double" },
                { name: "uniquePos", type: "string" },
            ],
            labelingInfo: [depthLabel],
            renderer: {
                type: "simple",
                symbol: {
                    type: "simple-marker",
                    size: 0,
                    color: [0, 0, 255, 0.7],
                    outline: { color: [0, 0, 0, 0.5], width: 0.5 },
                },
            },
            spatialReference: mapview.spatialReference,
            geometryType: "point",
            minScale: 30,
            maxScale: 0,
            visible: false,
        });
        mapview.map.add(depthLayer);
    } else {
        const existingFeatures = await depthLayer.queryFeatures({
            where: "1=1",
            returnGeometry: false,
            outFields: ["uniquePos", "objectid"],
        });

        const existingIds = new Set(existingFeatures.features.map((f) => f.attributes.uniquePos));
        const newPoints = points.filter((p) => !existingIds.has(p.attributes.uniquePos));

        // Expand extent to keep points around focus area
        const extendedExtent = mapview.extent.clone();
        extendedExtent.expand(2);
        const featuresNotInView = await depthLayer.queryFeatures({
            geometry: extendedExtent,
            spatialRelationship: "disjoint",
            returnGeometry: false,
            outFields: ["uniquePos", "objectid"],
        });

        depthLayer.applyEdits({ addFeatures: newPoints, deleteFeatures: featuresNotInView.features });
    }
};

/**
 * Queries depth values for a given extent and renders them to the map as labels.

 * @param extent 
 * @returns 
 */
export const queryKYVHighResDepth = async (extent: Extent) => {
    const mapview = getMapView();

    if (!mapview?.map) throw new Error("No mapview");
    if (mapview.scale >= 200) {
        renderKYVDepthPointsToMap([]);
        return;
    }

    const mapviewDepthLayer = mapview.map.findLayerById("highResKYVDepthLayer") as FeatureLayer;
    if (mapviewDepthLayer) {
        if (!mapviewDepthLayer.visible) {
            return;
        }
    }

    const depthLayerUrl = AppConfig.KyvDepthFeatureServerUrl;
    if (!depthLayerUrl) {
        toast.error("Kystverkets dybdedata tjeneste er ikke konfigurert.");
        return;
    }
    const depthLayer = new FeatureLayer({
        url: depthLayerUrl,
    });
    if (!depthLayer.loaded) {
        await depthLayer.load();
    }

    const depthQuery = depthLayer.createQuery();
    depthQuery.geometry = extent;
    depthQuery.returnGeometry = true;
    depthQuery.returnZ = true;
    depthQuery.spatialRelationship = "intersects";
    depthQuery.outFields = ["*"];

    const depths = await depthLayer.queryFeatures(depthQuery);

    // We get n-multipoints back from the service, need to split them into points
    const features: Graphic[] = [];
    if (!depths.features) {
        return;
    }
    const maxFeaturesToRender = 10000;
    const uniquePositions = new Set<string>();
    for (const feature of depths.features) {
        const geometry = feature.geometry as Multipoint;
        const pointValues = geometry.points;
        for (const element of pointValues) {
            const point = element;
            if (point[2] === -9999 || point[2] === null || point[2] === undefined) continue; // No data value
            // Using x_y_ as objectid to avoid duplicates
            if (features.length >= maxFeaturesToRender) break;
            const posKey = `${point[0]}_${point[1]}`;
            if (uniquePositions.has(posKey)) continue;
            uniquePositions.add(posKey);
            features.push(
                new Graphic({
                    geometry: new Point({ x: point[0], y: point[1], spatialReference: extent.spatialReference }),
                    attributes: { dybde: point[2], uniquePos: `${point[0]}_${point[1]}` },
                })
            );
        }
        if (features.length >= maxFeaturesToRender) {
            console.warn("Reached max label features to render");
            break;
        }
    }

    renderKYVDepthPointsToMap(features);
};

/**
 * Renders the depth values to the map as points in a feature layer
 */
export const renderKYVDepthPointsToMap = async (points: Graphic[]) => {
    const mapview = getMapView();
    if (!mapview?.map) throw new Error("No mapview");

    let depthLayer = mapview.map.findLayerById("highResKYVDepthLayer") as FeatureLayer;

    const depthLabel = new LabelClass({
        labelExpressionInfo: {
            expression: "return Round($feature.dybde, 2)",
        },
        deconflictionStrategy: "static",
        labelPlacement: "center-center",
    });

    if (!depthLayer) {
        depthLayer = new FeatureLayer({
            id: "highResKYVDepthLayer",
            source: points,
            outFields: ["*"],
            objectIdField: "objectid",
            title: "Kystverkets dydbdeverdier (punkter)",
            fields: [
                { name: "objectid", type: "oid" },
                { name: "dybde", type: "double" },
                { name: "uniquePos", type: "string" },
            ],
            labelingInfo: [depthLabel],
            renderer: {
                type: "simple",
                symbol: {
                    type: "simple-marker",
                    size: 0,
                    color: [0, 0, 255, 0.7],
                    outline: { color: [0, 0, 0, 0.5], width: 0.5 },
                },
            },
            spatialReference: new SpatialReference({ wkid: 32633 }),
            geometryType: "point",
            minScale: 30,
            maxScale: 0,
            visible: false,
        });
        mapview.map.add(depthLayer);
    } else {
        const existingFeatures = await depthLayer.queryFeatures({
            where: "1=1",
            returnGeometry: false,
            outFields: ["uniquePos", "objectid"],
        });

        const existingIds = new Set(existingFeatures.features.map((f) => f.attributes.uniquePos));
        const newPoints = points.filter((p) => !existingIds.has(p.attributes.uniquePos));

        // Expand extent to keep points around focus area
        const extendedExtent = mapview.extent.clone();
        extendedExtent.expand(2);
        const featuresNotInView = await depthLayer.queryFeatures({
            geometry: extendedExtent,
            spatialRelationship: "disjoint",
            returnGeometry: false,
            outFields: ["uniquePos", "objectid"],
        });

        depthLayer.applyEdits({ addFeatures: newPoints, deleteFeatures: featuresNotInView.features });
    }
};
